// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Alert, AppIcon, Spinner, TextInput, TablePagination, TagChipGroup, TagFilterSelect } from '@loa/ui-components';
import { policyAggregateApi } from '../api/policyAggregateApi';
import { relationshipApi } from '../api/relationshipApi';
import { ViewToggle } from '../components/ViewToggle';
import { AdvisorFilter } from '../components/AdvisorFilter';
import { useAdminViewToggle } from '../hooks/useAdminViewToggle';
import { useTags } from '../contexts/TagsContext';
import { useEntityTags } from '../hooks/useEntityTags';
import type { PolicyListDTO, Page } from '../types/policyList';
import './PoliciesListPage.css';

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// SVG Icons
const Icons = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  filter: (
    <svg className="policies-filter-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
    </svg>
  ),
  view: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  check: (
    <svg className="status-badge__icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  ),
  user: (
    <svg className="ownership-badge__icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  ),
  users: (
    <svg className="ownership-badge__icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  ),
};

// Get status badge class
function getStatusClass(status: string): string {
  const statusMap: Record<string, string> = {
    ACTIVE: 'status-badge--active',
    DRAFT: 'status-badge--draft',
    PENDING_APPROVAL: 'status-badge--pending',
    SUSPENDED: 'status-badge--suspended',
    CANCELLED: 'status-badge--cancelled',
    EXPIRED: 'status-badge--expired',
  };
  return statusMap[status] || 'status-badge--draft';
}

// Format status display
function formatStatus(status: string): string {
  return status.replaceAll('_', ' ').toLowerCase().replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

export function PoliciesListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  // (Four filter-dropdown `setShow…` setters that fed an earlier
  // never-read state were removed with the values they backed; the
  // setters' previous callsites in the dropdowns were similarly cleaned
  // up below.)

  // Data state
  const [data, setData] = useState<Page<PolicyListDTO> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch relationships for dropdown
  const [relationships, setRelationships] = useState<Array<{ key: string; text: string; value: string }>>([]);

  // Admin view toggle hook
  const {
    currentView,
    setCurrentView,
    isAdminView,
    showToggle,
    isInitializing
  } = useAdminViewToggle({
    storageKey: 'policies',
    syncWithUrl: true
  });

  // State for advisor filter (only used in admin view)
  const [advisorFilter, setAdvisorFilter] = useState<string>('all');

  // Tag filter (Tags PRD FR-13, ANY-of). NOTE: the underlying
  // PolicyAggregateController endpoint does not yet bind tagIds server-side
  // — see the comment on PolicyListFilters.tagIds; the filter UI ships now
  // so no further UI change is needed once that backend gap is closed.
  const { tags: allTags, featureEnabled: tagsFeatureEnabled } = useTags();
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([]);

  // Ref to track if this is the initial load
  const isInitialLoad = useRef(true);

  // Load relationships for dropdown
  useEffect(() => {
    const loadRelationships = async () => {
      try {
        const response = isAdminView
          ? await relationshipApi.getAllRelationships(0, 100)
          : await relationshipApi.getRelationships({ page: 0, size: 100 });
        const relationshipOptions = response.content.map((r) => ({
          key: r.id,
          text: r.profileName || 'Unnamed Relationship',
          value: r.id,
        }));
        setRelationships([
          { key: 'all', text: 'All Relationships', value: '' },
          ...relationshipOptions,
        ]);
      } catch (err) {
        console.error('Failed to load relationships:', err);
      }
    };

    loadRelationships();
  }, [isAdminView]);

  // Load policies
  const loadPolicies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters = {
        searchQuery: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        profileId: relationshipFilter || undefined,
        ownershipType: ownershipFilter || undefined,
        sortBy: sortColumn,
        sortDirection: sortDirection,
        page: currentPage,
        size: itemsPerPage,
        all: isAdminView || undefined,
        advisorId: isAdminView && advisorFilter !== 'all' ? advisorFilter : undefined,
        tagIds: tagFilterIds.length > 0 ? tagFilterIds : undefined,
      };

      const response = await policyAggregateApi.getPolicies(filters);
      setData(response);
    } catch (err) {
      console.error('Failed to load policies:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, categoryFilter, statusFilter, relationshipFilter, ownershipFilter, sortColumn, sortDirection, currentPage, itemsPerPage, isAdminView, advisorFilter, tagFilterIds]);

  // Load policies when filters change
  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, categoryFilter, statusFilter, relationshipFilter, ownershipFilter, advisorFilter, itemsPerPage, tagFilterIds]);

  // Reset filters, pagination when view changes
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    setCurrentPage(0);
    setSearchQuery('');
    setCategoryFilter('');
    setStatusFilter('');
    setRelationshipFilter('');
    setOwnershipFilter('');
    setAdvisorFilter('all');
    setSortColumn('createdAt');
    setSortDirection('desc');
    setTagFilterIds([]);
  }, [currentView]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getProviderInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const policies = data?.content || [];
  const totalPages = data?.totalPages || 0;
  const totalElements = data?.totalElements || 0;

  // Batch tag decoration — ONE call per page of rows (never per-row).
  const policyIds = policies.map((p) => p.id);
  const { tagsByEntityId } = useEntityTags('POLICY', policyIds);

  const itemsPerPageOptions = [5, 8, 10, 20];

  const statusOptions = [
    { key: 'all', text: 'All Statuses', value: '' },
    { key: 'draft', text: 'Draft', value: 'DRAFT' },
    { key: 'pending', text: 'Pending Approval', value: 'PENDING_APPROVAL' },
    { key: 'active', text: 'Active', value: 'ACTIVE' },
    { key: 'suspended', text: 'Suspended', value: 'SUSPENDED' },
    { key: 'cancelled', text: 'Cancelled', value: 'CANCELLED' },
    { key: 'expired', text: 'Expired', value: 'EXPIRED' },
  ];

  const categoryOptions = [
    { key: 'all', text: 'All Categories', value: '' },
    { key: 'insurance', text: 'Insurance', value: 'INSURANCE' },
    { key: 'investment', text: 'Investment', value: 'INVESTMENT' },
    { key: 'banking', text: 'Banking', value: 'BANKING' },
    { key: 'pension', text: 'Pension', value: 'PENSION' },
  ];

  const ownershipOptions = [
    { key: 'all', text: 'All Ownership Types', value: '' },
    { key: 'solo', text: 'Solo', value: 'SOLO' },
    { key: 'joint', text: 'Joint', value: 'JOINT' },
    { key: 'corporate', text: 'Corporate', value: 'CORPORATE' },
  ];

  return (
    <div className="policies-list-page">
      {/* Page Header */}
      <div className="policies-page-header">
        <h1 className="policies-page-header__title">
          Policies{' '}
          <span className="policies-page-header__count">{totalElements}</span>
        </h1>
        <p className="policies-page-header__subtitle">
          {isAdminView
            ? 'All policies across the brokerage'
            : 'All policies across your portfolio'}
        </p>
      </div>

      {/* View Toggle - Only shown for admin users */}
      {showToggle && (
        <div style={{ marginBottom: '1rem' }}>
          <ViewToggle
            currentView={currentView}
            onViewChange={setCurrentView}
            disabled={isInitializing || isLoading}
          />
        </div>
      )}

      {/* Admin Advisor Filter */}
      {isAdminView && (
        <div style={{ marginBottom: '1rem' }}>
          <AdvisorFilter
            value={advisorFilter}
            onChange={setAdvisorFilter}
            disabled={isLoading}
            placeholder="Filter by Advisor"
          />
        </div>
      )}

      {/* Main Card */}
      <div className="policies-card">
        <div className="policies-card__header">
          <h2 className="policies-card__title">Manage Policies</h2>
        </div>

        {/* Search and Filters */}
        <div className="policies-filters">
          <div className="policies-search">
            <TextInput
              iconLeft={<AppIcon name="search" size={16} color="#333333" />}
              placeholder="Search by policy number Eg: POL-123-456"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fluid
            />
          </div>

          <Dropdown
            className={`policies-filter-btn ${categoryFilter ? 'active' : ''}`}
            placeholder="Filter By Category"
            options={categoryOptions.map(o => ({ value: o.value, text: o.text }))}
            value={categoryFilter}
            onChange={(value) => { setCategoryFilter(value); }}
            clearable
          />

          <Dropdown
            className={`policies-filter-btn ${statusFilter ? 'active' : ''}`}
            placeholder="Filter By Status"
            options={statusOptions.map(o => ({ value: o.value, text: o.text }))}
            value={statusFilter}
            onChange={(value) => { setStatusFilter(value); }}
            clearable
          />

          <Dropdown
            className={`policies-filter-btn ${ownershipFilter ? 'active' : ''}`}
            placeholder="Filter By Ownership"
            options={ownershipOptions.map(o => ({ value: o.value, text: o.text }))}
            value={ownershipFilter}
            onChange={(value) => { setOwnershipFilter(value); }}
            clearable
          />

          <Dropdown
            className={`policies-filter-btn ${relationshipFilter ? 'active' : ''}`}
            placeholder="Filter By Relationship"
            options={relationships.map(o => ({ value: o.value, text: o.text }))}
            value={relationshipFilter}
            onChange={(value) => { setRelationshipFilter(value); }}
            search
            clearable
          />

          {tagsFeatureEnabled && (
            <TagFilterSelect
              options={allTags.filter((t) => !t.archived).map((t) => ({ id: t.id, name: t.name, color: t.color }))}
              selectedIds={tagFilterIds}
              onChange={setTagFilterIds}
              disabled={isLoading}
              data-testid="policies-tag-filter"
            />
          )}
        </div>

        {/* Table */}
        <div className="policies-table-container">
          <table className="policies-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Category</th>
                <th>Policy Number</th>
                <th>Relationship</th>
                <th>Status</th>
                <th>Ownership</th>
                <th className="center">Owners</th>
                <th className="center">Signatories</th>
                <th>Created</th>
                {tagsFeatureEnabled && <th>Tags</th>}
                <th className="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={tagsFeatureEnabled ? 11 : 10}>
                    <div className="policies-loading">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan={tagsFeatureEnabled ? 11 : 10}>
                    <div className="policies-error">
                      <Alert variant="error" header="Error Loading Policies">
                        <p>{error.message || 'Failed to load policies'}</p>
                      </Alert>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !error && policies.length === 0 && (
                <tr>
                  <td colSpan={tagsFeatureEnabled ? 11 : 10}>
                    <div className="policies-empty">
                      <h3 className="policies-empty__title">No Policies Found</h3>
                      <p className="policies-empty__description">
                        {searchQuery || categoryFilter || statusFilter || relationshipFilter || ownershipFilter || tagFilterIds.length > 0
                          ? 'No policies match your search criteria. Try adjusting your filters.'
                          : 'No policies have been added yet.'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !error && policies.map((policy) => (
                <tr key={policy.id} onClick={() => navigate(`/policies/${policy.id}`)}>
                  <td>
                    <div className="provider-cell">
                      <div className="provider-avatar">
                        {getProviderInitials(policy.providerName)}
                      </div>
                      <span className="provider-name" title={policy.providerName}>
                        {policy.providerName}
                      </span>
                    </div>
                  </td>
                  <td>
                    {policy.category ? (
                      <span className="policy-type">{policy.category}</span>
                    ) : (
                      <span className="policy-type--empty">-</span>
                    )}
                  </td>
                  <td>
                    {policy.policyNumber ? (
                      <span className="policy-number">{policy.policyNumber}</span>
                    ) : (
                      <span className="policy-number--empty">-</span>
                    )}
                  </td>
                  <td>
                    <span className="relationship-name">{policy.profileName || 'Unknown'}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(policy.status)}`}>
                      {Icons.check}
                      {formatStatus(policy.status)}
                    </span>
                  </td>
                  <td>
                    <span className="ownership-badge">
                      {policy.ownershipType === 'JOINT' ? Icons.users : Icons.user}
                      {policy.ownershipType === 'SOLO' ? 'Solo' : policy.ownershipType}
                    </span>
                  </td>
                  <td className="center">
                    <span className="count-cell">{policy.ownerCount}</span>
                  </td>
                  <td className="center">
                    <span className="count-cell">{policy.signatoryCount}</span>
                  </td>
                  <td>
                    <span className="date-cell">{formatDate(policy.createdAt)}</span>
                  </td>
                  {tagsFeatureEnabled && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <TagChipGroup
                        tags={(tagsByEntityId[policy.id] ?? []).map((t) => ({
                          id: t.id, name: t.name, color: t.color, archived: t.archived,
                        }))}
                      />
                    </td>
                  )}
                  <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="action-btn"
                      onClick={() => navigate(`/policies/${policy.id}`)}
                      title="View policy"
                    >
                      {Icons.view}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && !error && policies.length > 0 && (
          <TablePagination
            config={{
              currentPage: currentPage + 1,
              totalPages: totalPages || 1,
              itemsPerPage,
              itemsPerPageOptions,
              onPageChange: (next) => setCurrentPage(next - 1),
              onItemsPerPageChange: (size) => {
                setItemsPerPage(size);
                setCurrentPage(0);
              },
            }}
          />
        )}
      </div>
    </div>
  );
}
